// src/components/CustomizeTemplate.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { api } from '../services/api';

// --- Register Custom Fonts & Sizes ---
const Font = Quill.import('formats/font');
Font.whitelist = [
  'arial', 'comic-sans', 'courier-new', 'georgia',
  'helvetica', 'lucida', 'times-new-roman', 'trebuchet-ms', 'verdana',
];
Quill.register(Font, true);

const Size = Quill.import('attributors/style/size');
export const customSizes = [
  '8px', '9px', '10px', '11px', '12px', '14px', '16px',
  '18px', '20px', '22px', '24px', '26px', '28px',
  '36px', '48px', '72px',
];
export const toolbarSizes = [false, ...customSizes];
Size.whitelist = customSizes;
Quill.register(Size, true);

const icons = Quill.import('ui/icons');
icons['insertTable'] =
  '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="3" y1="15" x2="21" y2="15"></line><line x1="9" y1="3" x2="9" y2="21"></line><line x1="15" y1="3" x2="15" y2="21"></line></svg>';

const TEMPLATE_TABS = [
  'Abdomen', 'Pelvis', 'Obs', 'AbdPel Male', 'AbdPel Female',
  'Breast', 'MSK', 'Neck', 'Doppler', 'General',
];

const TAB_TO_ENUM_MAP = {
  Abdomen: 'ABDOMINAL',
  Pelvis: 'PELVIC_FEMALE',
  Obs: 'OBSTETRIC_EARLY',
  'AbdPel Male': 'ABDOMEN_PELVIS_MALE',
  'AbdPel Female': 'ABDOMEN_PELVIS_FEMALE',
  Breast: 'BREAST',
  MSK: 'MUSCULOSKELETAL',
  Neck: 'NECK',
  Doppler: 'ARTERIAL_DOPPLER_BOTH_LOWER',
  General: 'GENERAL',
};

const DEFAULT_FINDINGS_HTML =
  '<b>Clinical History:</b> [insert clinical history]<br><br><b>Technique:</b><br>Ultrasound examination was performed.<br><br><b>Findings:</b><ul><li>Enter your default findings here.</li></ul>';

const useViewport = () => {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return {
    width,
    isMobile: width < 640,
    isTablet: width >= 640 && width < 1024,
    isSmallDesktop: width >= 1024 && width < 1280,
  };
};

const readLS = (key, fallback = '') => {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
};

const CustomizeTemplate = ({ onBack, userProfile }) => {
  const templateRef = useRef(null);
  const { isMobile, isTablet } = useViewport();

  // Prefer blob URL if we already have one; fall back to raw URL.
  const [letterheadUrl, setLetterheadUrl] = useState(() =>
    readLS('letterheadBlobUrl') || readLS('letterheadUrl')
  );
  const [signatureUrl, setSignatureUrl] = useState(() =>
    readLS('signatureBlobUrl') || readLS('signatureUrl')
  );
  const [hospitalName, setHospitalName] = useState(() =>
    readLS('hospitalName')
  );
  const [clinicianName, setClinicianName] = useState(() => {
    const fromProfile =
      userProfile?.fullName ||
      [userProfile?.firstName, userProfile?.lastName]
        .filter(Boolean)
        .join(' ');
    return (
      fromProfile ||
      readLS('clinicianName') ||
      readLS('clinicianFullName')
    );
  });
  const [clinicianDesignation, setClinicianDesignation] = useState(() => {
    return userProfile?.designation || readLS('clinicianDesignation');
  });

  const [savedTemplates, setSavedTemplates] = useState([]);

  // ─── REACTIVE SYNC + AUTHENTICATED BLOB RESOLUTION ───
  useEffect(() => {
    let cancelled = false;

    const syncFromStorage = () => {
      setLetterheadUrl(readLS('letterheadBlobUrl') || readLS('letterheadUrl'));
      setSignatureUrl(readLS('signatureBlobUrl') || readLS('signatureUrl'));
      setHospitalName(readLS('hospitalName'));

      const fromProfile =
        userProfile?.fullName ||
        [userProfile?.firstName, userProfile?.lastName]
          .filter(Boolean)
          .join(' ');

      setClinicianName(
        fromProfile || readLS('clinicianName') || readLS('clinicianFullName')
      );
      setClinicianDesignation(
        userProfile?.designation || readLS('clinicianDesignation')
      );
    };

    const resolveAuthenticatedImages = async () => {
      // Letterhead
      try {
        const blobUrl = await api.fetchLetterheadBlobUrl();
        if (!cancelled && blobUrl) {
          setLetterheadUrl(blobUrl);
          try { localStorage.setItem('letterheadBlobUrl', blobUrl); } catch (_) {}
        }
      } catch (e) {
        console.warn('[CustomizeTemplate] letterhead resolve failed:', e.message);
      }

      // Signature
      try {
        const raw = readLS('signatureUrl');
        const blobUrl = await api.fetchSignatureBlobUrl(raw);
        if (!cancelled && blobUrl) {
          setSignatureUrl(blobUrl);
          try { localStorage.setItem('signatureBlobUrl', blobUrl); } catch (_) {}
        }
      } catch (e) {
        console.warn('[CustomizeTemplate] signature resolve failed:', e.message);
      }
    };

    syncFromStorage();
    resolveAuthenticatedImages();

    const onSettingsUpdated = () => {
      syncFromStorage();
      resolveAuthenticatedImages();
    };

    window.addEventListener('settings-updated', onSettingsUpdated);
    window.addEventListener('storage', syncFromStorage);
    window.addEventListener('focus', onSettingsUpdated);
    document.addEventListener('visibilitychange', onSettingsUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener('settings-updated', onSettingsUpdated);
      window.removeEventListener('storage', syncFromStorage);
      window.removeEventListener('focus', onSettingsUpdated);
      document.removeEventListener('visibilitychange', onSettingsUpdated);
    };
  }, [userProfile]);

  useEffect(() => {
    const workspace = document.querySelector('.workspace');
    if (workspace) workspace.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadTemplates = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn(
          '[CustomizeTemplate] No authToken in localStorage — skipping backend fetch'
        );
        return;
      }
      try {
        const res = await api.getTemplates();
        const list = res?.data?.content || res?.data || [];
        if (!cancelled) {
          setSavedTemplates(Array.isArray(list) ? list : []);
        }
      } catch (err) {
        console.error('[CustomizeTemplate] getTemplates failed:', err.message);
      }
    };
    loadTemplates();
    return () => { cancelled = true; };
  }, []);

  const loadState = (key, fallback) => {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  };

  const [tableModal, setTableModal] = useState({ isOpen: false, quill: null });
  const [tableCols, setTableCols] = useState(5);
  const [tableRows, setTableRows] = useState(2);
  const [rememberDims, setRememberDims] = useState(false);

  const [tableUI, setTableUI] = useState({
    visible: false,
    top: 0,
    left: 0,
    quillRef: null,
    tableNode: null,
  });
  const [tableMenuOpen, setTableMenuOpen] = useState(false);

  useEffect(() => {
    const savedCols = localStorage.getItem('echion_table_cols_template');
    const savedRows = localStorage.getItem('echion_table_rows_template');
    if (savedCols) setTableCols(parseInt(savedCols, 10));
    if (savedRows) setTableRows(parseInt(savedRows, 10));
  }, []);

  useEffect(() => {
    const handleTableInteract = (e) => {
      if (e.target.closest('.table-ui-menu')) return;
      const td = e.target.closest('td');
      if (td) {
        const table = td.closest('table');
        const qRef = td.closest('#editor-template') ? templateRef : null;
        if (table && qRef) {
          const rect = table.getBoundingClientRect();
          setTableUI({
            visible: true,
            top: rect.top - 12,
            left: rect.left - 12,
            quillRef: qRef,
            tableNode: table,
          });
          setTableMenuOpen(false);
          return;
        }
      }
      setTableUI((prev) => ({ ...prev, visible: false }));
      setTableMenuOpen(false);
    };

    const updateOverlayPos = () => {
      setTableUI((prev) => {
        if (prev.visible && prev.tableNode) {
          const rect = prev.tableNode.getBoundingClientRect();
          return { ...prev, top: rect.top - 12, left: rect.left - 12 };
        }
        return prev;
      });
    };

    document.addEventListener('mousedown', handleTableInteract);
    window.addEventListener('scroll', updateOverlayPos, true);
    window.addEventListener('resize', updateOverlayPos);

    return () => {
      document.removeEventListener('mousedown', handleTableInteract);
      window.removeEventListener('scroll', updateOverlayPos, true);
      window.removeEventListener('resize', updateOverlayPos);
    };
  }, []);

  const [activeTab, setActiveTab] = useState(() =>
    loadState('ct_activeTab', 'AbdPel Female')
  );
  const [editorContent, setEditorContent] = useState(() =>
    loadState('ct_content', DEFAULT_FINDINGS_HTML)
  );
  const [generalTitle, setGeneralTitle] = useState(() =>
    loadState('ct_generalTitle', 'ULTRASOUND GENERAL REPORT')
  );
  const [statusMsg, setStatusMsg] = useState('');

  useEffect(() => {
    localStorage.setItem('ct_activeTab', JSON.stringify(activeTab));
  }, [activeTab]);
  useEffect(() => {
    localStorage.setItem('ct_content', JSON.stringify(editorContent));
  }, [editorContent]);
  useEffect(() => {
    localStorage.setItem('ct_generalTitle', JSON.stringify(generalTitle));
  }, [generalTitle]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const scanTypeEnum = TAB_TO_ENUM_MAP[tab];
    const existing = savedTemplates.find((t) => t.scanType === scanTypeEnum);
    if (existing) {
      if (existing.defaultFindings) setEditorContent(existing.defaultFindings);
      if (tab === 'General' && existing.name) setGeneralTitle(existing.name);
    } else {
      setEditorContent(DEFAULT_FINDINGS_HTML);
    }
  };

  const handleSaveTemplate = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setStatusMsg('❌ Not logged in. Please log in first.');
      return;
    }
    setStatusMsg('Saving template...');
    try {
      const payload = {
        name:
          activeTab === 'General'
            ? generalTitle
            : `Ultrasound ${activeTab} Template`,
        description: `Custom template created for ${activeTab}`,
        gender: activeTab.includes('Male')
          ? 'MALE'
          : activeTab.includes('Female')
          ? 'FEMALE'
          : 'OTHER',
        reportType: 'NORMAL',
        scanType: TAB_TO_ENUM_MAP[activeTab],
        category: 'Custom',
        defaultFindings: editorContent,
        defaultImpression: '<p>Normal scan. No acute findings.</p>',
        tags: ['Custom', activeTab],
        isDefault: false,
      };
      await api.saveTemplate(payload);
      const refreshed = await api.getTemplates();
      const list = refreshed?.data?.content || refreshed?.data || [];
      setSavedTemplates(Array.isArray(list) ? list : []);
      setStatusMsg('✅ Template saved successfully!');
      setTimeout(() => setStatusMsg(''), 3000);
    } catch (err) {
      let msg = err.message;
      if (/permission|403|access denied/i.test(msg)) {
        msg =
          '❌ Access denied. Your role may not have permission to create templates. Try logging in as an ADMIN or HOSPITAL_ADMIN.';
      } else if (/session|401|expired/i.test(msg)) {
        msg = '❌ Session expired. Please log in again.';
      }
      setStatusMsg(msg);
    }
  };

  const executeInsertTable = () => {
    const quill = tableModal.quill;
    if (quill && tableRows > 0 && tableCols > 0) {
      if (rememberDims) {
        localStorage.setItem('echion_table_cols_template', tableCols);
        localStorage.setItem('echion_table_rows_template', tableRows);
      } else {
        localStorage.removeItem('echion_table_cols_template');
        localStorage.removeItem('echion_table_rows_template');
      }
      const tableWidth = '100%';
      let tableHTML = `<table style="width: ${tableWidth}; border-collapse: collapse; border: 1px solid #cbd5e1; margin-bottom: 10px;"><tbody>`;
      for (let i = 0; i < tableRows; i++) {
        tableHTML += '<tr>';
        for (let j = 0; j < tableCols; j++) {
          tableHTML +=
            '<td style="border: 1px solid #cbd5e1; padding: 8px; min-width: 50px;">&nbsp;</td>';
        }
        tableHTML += '</tr>';
      }
      tableHTML += '</tbody></table><p><br></p>';
      const range = quill.getSelection(true);
      quill.clipboard.dangerouslyPasteHTML(
        range ? range.index : quill.getLength(),
        tableHTML
      );
    }
    setTableModal({ isOpen: false, quill: null });
  };

  const handleTableAction = (action) => {
    const quill = tableUI.quillRef?.current?.getEditor();
    const table = tableUI.tableNode;
    const selection = window.getSelection();
    if (!quill || !table) return;
    let td = null;
    let tr = null;
    if (selection.anchorNode) {
      const node = selection.anchorNode;
      td =
        node.nodeType === 1
          ? node.closest('td')
          : node.parentElement?.closest('td');
      tr = td?.closest('tr');
    }
    if (!td || !tr) {
      tr = table.rows[0];
      td = tr?.cells[0];
    }
    if (!tr || !td) return;
    const cellIndex = Array.from(tr.children).indexOf(td);
    const rowIndex = Array.from(table.rows).indexOf(tr);

    if (action === 'deleteRow') {
      table.deleteRow(rowIndex);
      if (table.rows.length === 0) table.remove();
    } else if (action === 'deleteColumn') {
      Array.from(table.rows).forEach((row) => row.deleteCell(cellIndex));
      if (table.rows[0]?.children.length === 0) table.remove();
    } else if (action === 'deleteTable') {
      table.remove();
      setTableUI((prev) => ({ ...prev, visible: false }));
    } else if (action === 'insertRowAbove' || action === 'insertRowBelow') {
      const newRow = table.insertRow(
        action === 'insertRowAbove' ? rowIndex : rowIndex + 1
      );
      for (let i = 0; i < tr.children.length; i++) {
        const newCell = newRow.insertCell(i);
        newCell.innerHTML = '&nbsp;';
        newCell.style.border = '1px solid #cbd5e1';
        newCell.style.padding = '8px';
        newCell.style.minWidth = '50px';
      }
    } else if (
      action === 'insertColumnLeft' ||
      action === 'insertColumnRight'
    ) {
      const insertIdx =
        action === 'insertColumnLeft' ? cellIndex : cellIndex + 1;
      Array.from(table.rows).forEach((row) => {
        const newCell = row.insertCell(insertIdx);
        newCell.innerHTML = '&nbsp;';
        newCell.style.border = '1px solid #cbd5e1';
        newCell.style.padding = '8px';
        newCell.style.minWidth = '50px';
      });
    }
    quill.update('user');
    setTableMenuOpen(false);
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ font: Font.whitelist }, { size: toolbarSizes }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ script: 'sub' }, { script: 'super' }],
          [{ color: [] }, { background: [] }],
          [
            { list: 'ordered' },
            { list: 'bullet' },
            { indent: '-1' },
            { indent: '+1' },
          ],
          [{ align: [] }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['insertTable'],
          ['clean'],
        ],
        handlers: {
          insertTable: function () {
            setTableModal({ isOpen: true, quill: this.quill });
          },
        },
      },
      table: true,
    }),
    []
  );

  const stackHeader = isMobile || isTablet;
  const canvasPadding = isMobile
    ? '16px'
    : isTablet
    ? '24px'
    : '30px 40px';
  const editorOuterMargin = isMobile
    ? '0 -16px'
    : isTablet
    ? '0 -24px'
    : '0 -40px';
  const titleWidth = isMobile ? '100%' : '80%';

  return (
    <div
      className="fade-in"
      style={{
        width: '100%',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        padding: isMobile ? '8px' : '10px',
        paddingBottom: '100px',
        boxSizing: 'border-box',
        maxWidth: '100vw',
        overflowX: 'hidden',
      }}
    >
      <style>{`
        .ql-editor {
          min-height: 280px;
          overflow-wrap: break-word !important;
          word-wrap: break-word !important;
          word-break: break-word !important;
          font-family: Arial, sans-serif;
          color: #1e293b;
        }
        .ql-toolbar.ql-snow {
          border-top: 1px solid #e2e8f0;
          border-bottom: 1px solid #e2e8f0;
          border-left: none;
          border-right: none;
          background: #f8fafc;
          flex-wrap: wrap;
        }
        .ql-container.ql-snow { border: none; }

        .template-tabs::-webkit-scrollbar { height: 4px; }
        .template-tabs::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .template-tabs { -webkit-overflow-scrolling: touch; }

        .ql-snow .ql-toolbar button,
        .ql-snow .ql-toolbar .ql-picker { position: relative !important; }
        .ql-snow .ql-toolbar button::after,
        .ql-snow .ql-toolbar .ql-picker::after {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-top: 5px;
          background: #1e293b;
          color: #f8fafc;
          padding: 5px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
          white-space: nowrap;
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transition: opacity 0.2s ease, visibility 0.2s ease;
          z-index: 1000;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }
        .ql-snow .ql-toolbar button:hover::after,
        .ql-snow .ql-toolbar .ql-picker:hover::after { opacity: 1; visibility: visible; }

        .ql-snow .ql-toolbar .ql-font::after { content: "Font Style"; }
        .ql-snow .ql-toolbar .ql-size::after { content: "Font Size"; }
        .ql-snow .ql-toolbar .ql-bold::after { content: "Bold"; }
        .ql-snow .ql-toolbar .ql-italic::after { content: "Italic"; }
        .ql-snow .ql-toolbar .ql-underline::after { content: "Underline"; }
        .ql-snow .ql-toolbar .ql-strike::after { content: "Strikethrough"; }
        .ql-snow .ql-toolbar .ql-script[value="sub"]::after { content: "Subscript"; }
        .ql-snow .ql-toolbar .ql-script[value="super"]::after { content: "Superscript"; }
        .ql-snow .ql-toolbar .ql-color::after { content: "Text Color"; }
        .ql-snow .ql-toolbar .ql-background::after { content: "Highlight Color"; }
        .ql-snow .ql-toolbar .ql-list[value="ordered"]::after { content: "Numbered List"; }
        .ql-snow .ql-toolbar .ql-list[value="bullet"]::after { content: "Bulleted List"; }
        .ql-snow .ql-toolbar .ql-indent[value="-1"]::after { content: "Decrease Indent"; }
        .ql-snow .ql-toolbar .ql-indent[value="+1"]::after { content: "Increase Indent"; }
        .ql-snow .ql-toolbar .ql-align::after { content: "Text Alignment"; }
        .ql-snow .ql-toolbar .ql-header::after { content: "Format / Heading"; }
        .ql-snow .ql-toolbar button.ql-insertTable::after { content: "Insert Table"; }
        .ql-snow .ql-toolbar .ql-clean::after { content: "Clear Formatting"; }

        ${customSizes
          .map(
            (size) => `
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="${size}"]::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="${size}"]::before {
            content: '${size.replace('px', '')}' !important;
          }
        `
          )
          .join('\n')}

        .table-menu-btn {
          padding: 8px 15px;
          border: none;
          background: white;
          text-align: left;
          font-size: 13px;
          cursor: pointer;
          color: #334155;
          width: 100%;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background 0.2s;
        }
        .table-menu-btn:hover { background-color: #f1f5f9; }

        .ql-snow .ql-toolbar button.ql-insertTable {
          background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="%23444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="3" y1="15" x2="21" y2="15"></line><line x1="9" y1="3" x2="9" y2="21"></line><line x1="15" y1="3" x2="15" y2="21"></line></svg>');
          background-size: 16px;
          background-repeat: no-repeat;
          background-position: center;
        }
        .ql-snow .ql-toolbar button.ql-insertTable:hover {
          background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="%232563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="3" y1="15" x2="21" y2="15"></line><line x1="9" y1="3" x2="9" y2="21"></line><line x1="15" y1="3" x2="15" y2="21"></line></svg>');
        }
        .ql-snow .ql-toolbar button.ql-insertTable svg { display: none; }

        .ql-editor table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 10px;
        }
        .ql-editor td, .ql-editor th {
          border: 1px solid #cbd5e1;
          padding: 8px;
        }

        #editor-template .ql-editor { font-size: 12px; }

        #editor-template .ql-snow .ql-picker.ql-size .ql-picker-label:not([data-value])::before,
        #editor-template .ql-snow .ql-picker.ql-size .ql-picker-item:not([data-value])::before {
          content: '12' !important;
        }

        @media (max-width: 640px) {
          .ql-toolbar.ql-snow { padding: 4px !important; }
          .ql-snow .ql-toolbar button,
          .ql-snow .ql-toolbar .ql-picker {
            width: 26px !important;
            height: 22px !important;
          }
          .ql-snow .ql-toolbar button svg,
          .ql-snow .ql-toolbar .ql-picker svg {
            width: 14px !important;
            height: 14px !important;
          }
          .ql-snow .ql-picker.ql-expanded .ql-picker-options { max-width: 220px; }
        }
      `}</style>

      <div
        style={{
          display: 'flex',
          flexDirection: stackHeader ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: stackHeader ? 'stretch' : 'center',
          gap: stackHeader ? '12px' : '15px',
          marginBottom: '20px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2
            style={{
              margin: '0 0 8px 0',
              color: '#0f172a',
              fontSize: isMobile ? '1.2rem' : '1.4rem',
              fontWeight: '700',
            }}
          >
            Customize Report Templates
          </h2>
          <p
            style={{
              margin: 0,
              color: '#475569',
              fontSize: '0.9rem',
              lineHeight: '1.5',
            }}
          >
            Customize report templates for each scan type. Existing templates
            load automatically when you switch tabs.
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: stackHeader ? 'column' : 'row',
            alignItems: stackHeader ? 'stretch' : 'center',
            gap: '12px',
            flexShrink: 0,
          }}
        >
          {statusMsg && (
            <span
              style={{
                color: statusMsg.includes('❌') ? '#ef4444' : '#16a34a',
                fontSize: '0.85rem',
                fontWeight: '600',
                textAlign: stackHeader ? 'center' : 'left',
                wordBreak: 'break-word',
              }}
            >
              {statusMsg}
            </span>
          )}
          <button
            onClick={handleSaveTemplate}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: stackHeader ? '100%' : 'auto',
              whiteSpace: 'nowrap',
            }}
          >
            <svg
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
              />
            </svg>
            Save Template
          </button>
        </div>
      </div>

      <div
        className="template-tabs"
        style={{
          display: 'flex',
          overflowX: 'auto',
          borderBottom: '1px solid #e2e8f0',
          marginBottom: '25px',
          paddingBottom: '5px',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {TEMPLATE_TABS.map((tab) => (
          <div
            key={tab}
            onClick={() => handleTabChange(tab)}
            style={{
              padding: isMobile ? '8px 12px' : '10px 16px',
              cursor: 'pointer',
              fontWeight: activeTab === tab ? '600' : '500',
              color: activeTab === tab ? '#2563eb' : '#64748b',
              borderBottom:
                activeTab === tab
                  ? '2px solid #2563eb'
                  : '2px solid transparent',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
              fontSize: isMobile ? '0.85rem' : '0.95rem',
              flexShrink: 0,
            }}
          >
            {tab}
          </div>
        ))}
      </div>

      <h3
        style={{
          margin: '0 0 20px 0',
          color: '#1e293b',
          fontSize: isMobile ? '1rem' : '1.1rem',
          fontWeight: '600',
          wordBreak: 'break-word',
        }}
      >
        Edit {activeTab} Report Template
      </h3>

      <div
        style={{
          maxWidth: '900px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
        }}
      >
        <p
          style={{
            margin: 0,
            color: '#64748b',
            fontSize: '0.85rem',
            lineHeight: '1.4',
          }}
        >
          <strong>Note:</strong> To change the letterhead and clinician
          signature preloaded on these templates, please visit the{' '}
          <b>Settings</b> page.
        </p>

        <div
          style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
            padding: canvasPadding,
            boxShadow: '0 4px 10px rgba(0,0,0,0.03)',
            marginTop: '10px',
            position: 'relative',
            boxSizing: 'border-box',
            maxWidth: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              paddingBottom: '15px',
              marginBottom: '20px',
              gap: '12px',
            }}
          >
            {letterheadUrl ? (
              <img
                key={letterheadUrl}
                src={letterheadUrl}
                alt="Letterhead"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100px',
                  objectFit: 'contain',
                }}
                onError={(e) => {
                  console.warn(
                    '[CustomizeTemplate] Letterhead failed to load:',
                    letterheadUrl
                  );
                  setLetterheadUrl('');
                }}
              />
            ) : (
              <>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '15px' }}
                >
                  <img
                    src={process.env.PUBLIC_URL + '/echionLOGO.png'}
                    alt="Logo"
                    style={{
                      width: '50px',
                      height: '50px',
                      objectFit: 'contain',
                    }}
                  />
                  <div>
                    <h1
                      style={{
                        margin: 0,
                        color: '#1e293b',
                        fontSize: '18pt',
                        fontWeight: '800',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {hospitalName || 'Echion Health'}
                    </h1>
                    <p
                      style={{
                        margin: 0,
                        color: '#64748b',
                        fontSize: '9pt',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                      }}
                    >
                      Sonoscribe
                    </p>
                  </div>
                </div>
                <div
                  style={{
                    textAlign: isMobile ? 'left' : 'right',
                    fontSize: '9pt',
                    color: '#334155',
                    lineHeight: '1.5',
                  }}
                >
                  <div style={{ fontWeight: 'bold', color: '#1e293b' }}>
                    Contact Information
                  </div>
                  <div>📞 +233 54 843 2033</div>
                  <div style={{ color: '#2563eb' }}>
                    www.echionhealth.com
                  </div>
                </div>
              </>
            )}
          </div>

          {activeTab === 'General' ? (
            <input
              type="text"
              value={generalTitle}
              onChange={(e) => setGeneralTitle(e.target.value)}
              spellCheck={true}
              data-gramm="true"
              data-gramm_editor="true"
              data-enable-grammarly="true"
              style={{
                textAlign: 'center',
                margin: '0 auto 20px auto',
                fontSize: '11pt',
                letterSpacing: '1px',
                color: '#1e293b',
                textTransform: 'uppercase',
                fontWeight: 'bold',
                border: '1px dashed #cbd5e1',
                borderRadius: '4px',
                padding: '6px',
                width: titleWidth,
                display: 'block',
                outlineColor: '#3b82f6',
                background: '#f8fafc',
                boxSizing: 'border-box',
              }}
            />
          ) : (
            <h3
              style={{
                textAlign: 'center',
                margin: '0 0 20px 0',
                fontSize: isMobile ? '10pt' : '11pt',
                letterSpacing: '1px',
                color: '#1e293b',
                textTransform: 'uppercase',
                fontWeight: 'bold',
                wordBreak: 'break-word',
              }}
            >
              ULTRASOUND {activeTab.toUpperCase()} REPORT
            </h3>
          )}

          <div id="editor-template" style={{ margin: editorOuterMargin }}>
            <ReactQuill
              ref={templateRef}
              theme="snow"
              value={editorContent}
              onChange={setEditorContent}
              modules={modules}
              spellCheck={true}
            />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '30px',
              paddingTop: '20px',
            }}
          >
            <div
              style={{
                textAlign: 'center',
                width: isMobile ? '100%' : '220px',
              }}
            >
              <div
                style={{
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {signatureUrl ? (
                  <img
                    key={signatureUrl}
                    src={signatureUrl}
                    alt="Signature"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                    onError={(e) => {
                      console.warn(
                        '[CustomizeTemplate] Signature failed to load:',
                        signatureUrl
                      );
                      setSignatureUrl('');
                    }}
                  />
                ) : (
                  <span
                    style={{
                      fontFamily:
                        "'Brush Script MT', 'Cedarville Cursive', cursive",
                      fontSize: '20pt',
                      color: '#1e293b',
                    }}
                  >
                    {clinicianName || 'John Doe'}
                  </span>
                )}
              </div>
              <div style={{ paddingTop: '5px', marginTop: '5px' }}>
                <strong
                  style={{ display: 'block', fontSize: '10pt', color: '#000' }}
                >
                  {clinicianName ? `Dr. ${clinicianName}` : 'Dr. John Doe'}
                </strong>
                <span style={{ fontSize: '9pt', color: '#475569' }}>
                  {clinicianDesignation
                    ? clinicianDesignation.replace('_', ' ')
                    : 'Consultant Radiologist'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {tableUI.visible && (
        <div
          className="table-ui-menu"
          style={{
            position: 'fixed',
            top: tableUI.top,
            left: tableUI.left,
            zIndex: 10000,
          }}
        >
          <div
            onClick={() => setTableMenuOpen(!tableMenuOpen)}
            onMouseDown={(e) => e.preventDefault()}
            style={{
              width: '24px',
              height: '24px',
              background: 'white',
              border: '1px solid #cbd5e1',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
              color: '#475569',
            }}
            title="Table Options"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </div>

          {tableMenuOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '4px',
                background: 'white',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                width: '200px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <button
                className="table-menu-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTableAction('insertRowAbove')}
              >
                Insert Row Above
              </button>
              <button
                className="table-menu-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTableAction('insertRowBelow')}
              >
                Insert Row Below
              </button>
              <button
                className="table-menu-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTableAction('insertColumnLeft')}
              >
                Insert Column Left
              </button>
              <button
                className="table-menu-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTableAction('insertColumnRight')}
              >
                Insert Column Right
              </button>
              <div
                style={{
                  height: '1px',
                  background: '#e2e8f0',
                  margin: '4px 0',
                }}
              ></div>
              <button
                className="table-menu-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTableAction('deleteRow')}
                style={{ color: '#ef4444' }}
              >
                Delete Row
              </button>
              <button
                className="table-menu-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTableAction('deleteColumn')}
                style={{ color: '#ef4444' }}
              >
                Delete Column
              </button>
              <button
                className="table-menu-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTableAction('deleteTable')}
                style={{ color: '#ef4444', fontWeight: 'bold' }}
              >
                Delete Table
              </button>
            </div>
          )}
        </div>
      )}

      {tableModal.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.2)',
            padding: '12px',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              backgroundColor: '#f0f0f0',
              border: '1px solid #999',
              borderRadius: '6px',
              width: '100%',
              maxWidth: '300px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
              fontFamily: '"Segoe UI", sans-serif',
              fontSize: '12px',
              color: '#333',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 15px',
                backgroundColor: '#fff',
                borderBottom: '1px solid #ccc',
                borderTopLeftRadius: '6px',
                borderTopRightRadius: '6px',
              }}
            >
              <span style={{ fontSize: '13px', color: '#000' }}>
                Insert Table
              </span>
              <button
                onClick={() => setTableModal({ isOpen: false, quill: null })}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  color: '#666',
                  lineHeight: 1,
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: '15px' }}>
              <div style={{ color: '#000', marginBottom: '8px' }}>
                Table size
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '6px',
                  paddingLeft: '10px',
                }}
              >
                <span>
                  Number of <u>c</u>olumns:
                </span>
                <input
                  type="number"
                  value={tableCols}
                  onChange={(e) => setTableCols(e.target.value)}
                  style={{
                    width: '60px',
                    padding: '3px 5px',
                    border: '1px solid #ccc',
                  }}
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px',
                  paddingLeft: '10px',
                }}
              >
                <span>
                  Number of <u>r</u>ows:
                </span>
                <input
                  type="number"
                  value={tableRows}
                  onChange={(e) => setTableRows(e.target.value)}
                  style={{
                    width: '60px',
                    padding: '3px 5px',
                    border: '1px solid #ccc',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  <input
                    type="checkbox"
                    checked={rememberDims}
                    onChange={(e) => setRememberDims(e.target.checked)}
                  />
                  Remember dimensions for new tables
                </label>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '10px',
                }}
              >
                <button
                  onClick={executeInsertTable}
                  style={{
                    padding: '5px 25px',
                    border: '1px solid #0078d7',
                    backgroundColor: '#fff',
                    color: '#000',
                    borderRadius: '2px',
                    cursor: 'pointer',
                  }}
                >
                  OK
                </button>
                <button
                  onClick={() => setTableModal({ isOpen: false, quill: null })}
                  style={{
                    padding: '5px 25px',
                    border: '1px solid #ccc',
                    backgroundColor: '#fff',
                    color: '#000',
                    borderRadius: '2px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomizeTemplate;